type ResourceNode = Record<string, unknown>;

export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function copyNode(value: unknown): unknown {
  if (Array.isArray(value)) return [...value];
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, copyNode(child)])
    );
  }
  return value;
}

function mergeNodes(target: ResourceNode, source: ResourceNode) {
  for (const [key, value] of Object.entries(source)) {
    const current = target[key];
    if (!(key in target)) target[key] = copyNode(value);
    else if (Array.isArray(value) && Array.isArray(current)) {
      target[key] = [...current, ...value];
    } else if (
      value &&
      typeof value === 'object' &&
      current &&
      typeof current === 'object'
    ) {
      mergeNodes(current as ResourceNode, value as ResourceNode);
    }
  }
}

export function normalizeResourceData<
  T extends { subject: string[] } & Record<string, unknown>
>(data: T): T {
  const normalized: ResourceNode = { ...data };
  for (const subject of data.subject) {
    const levels = data[subject] as ResourceNode | undefined;
    if (!levels) continue;
    const merged: ResourceNode = {};
    for (const [level, resources] of Object.entries(levels)) {
      const name = ['GCSE', 'IGCSE', 'GCSE/IGCSE', 'GCSE_IGCSE'].includes(level)
        ? 'GCSE_IGCSE'
        : level;
      if (!merged[name]) merged[name] = copyNode(resources);
      else mergeNodes(merged[name] as ResourceNode, resources as ResourceNode);
    }
    normalized[subject] = merged;
  }
  return normalized as T;
}

export function getLegacyResourceRedirect(
  segments: string[],
  data: { subject: string[] } & Record<string, unknown>
): string | undefined {
  if (segments.length < 3 || segments.length > 4) return;
  if (!['past-paper', 'revision'].includes(segments[2])) return;
  const subject = data.subject.find((name) => slugify(name) === segments[0]);
  if (!subject) return;
  const levels = data[subject] as ResourceNode | undefined;
  const level = Object.keys(levels ?? {}).find(
    (name) => slugify(name) === segments[1]
  );
  if (!level) return;
  const boards = levels?.[level] as ResourceNode | undefined;
  const aqa = boards?.AQA as ResourceNode | undefined;
  const type = Object.keys(aqa ?? {}).find(
    (name) => slugify(name) === segments[2]
  );
  if (!type) return;
  return `/free-resources/${segments[0]}/${segments[1]}/aqa/${segments
    .slice(2)
    .join('/')}`;
}

export type ResourceTopic = {
  id: number;
  title: string;
  resourceS3Key?: string | null;
  link?: string | null;
};

export function getResourceTopicGroups(node?: {
  groups?: ResourceTopic[][];
  topic?: ResourceTopic[];
}): ResourceTopic[][] {
  if (node?.groups) return node.groups;
  return (
    node?.topic
      ?.filter(
        (topic) => topic.resourceS3Key || topic.link?.startsWith('https://')
      )
      .map((topic) => [topic]) ?? []
  );
}
